// Utilities & Hooks
import { Link, useNavigate } from "react-router";
import { useExtractSearchParameters } from "../../hooks/useExtractSearchParameters";
import { useAuthenticationAutologin } from "../../api/Authentication/Authentication";
import { LocalStorageActions } from "../../utilities/handleLocalStorage";
import { toast } from "react-toastify";
import { useEffect } from "react";
import { useAuth } from "../../providers/auth-context";
import useErrorReporting from "../../hooks/useErrorReporting";

// Components
import Loader from "../../components/Loader/Loader";

const Autologin = () => {
  const { handleAuthenticationTokens } = useAuth();
  const [searchParametersObject] = useExtractSearchParameters();
  const navigate = useNavigate();
  const existingAccessToken = LocalStorageActions.getItem("accessToken");
  const errorReporting = useErrorReporting();

  // Get the email from the search params & replace space character with "+"
  const formattedEmail = searchParametersObject?.email?.replaceAll(" ", "+") || "";

  // Send request to obtain new tokens from the used autologin link
  const { data, error, isPending, isSuccess } = useAuthenticationAutologin(
    formattedEmail,
    searchParametersObject.token,
    existingAccessToken,
  );

  // Updated authentication details with what is received from requested autologin
  // If an error occurs or if there's no valid access token, redirect to forbidden page
  useEffect(() => {
    if (!data) return;

    if (!data.access_token) {
      errorReporting("No token(s) received from the API", null);
      navigate("/403");
      return;
    }

    handleAuthenticationTokens({
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      expires_in: data.expires_in,
      autologin: data.autologin,
      long_lived: false,
    });
  }, [data]);

  // If there are any authentication values saved in local storage, redirect user to homepage
  useEffect(() => {
    if (!existingAccessToken) return;

    navigate("/applications/");
    toast.info(
      "You're already logged in. Try using the Autologin link in another browser or in incognito mode.",
      { ariaLabel: "autologin-info-notification" },
    );
  }, []);

  // If the autologin request has successfully been completed, redirect user to homepage
  useEffect(() => {
    if (!isSuccess) return;
    navigate("/applications/");
  }, [isSuccess]);

  if (error) {
    navigate("/403");
    return null;
  }

  // If there are missing "email" and/or "token" search parameters,
  // use a fallback UI to inform the user about the required fields
  if (!searchParametersObject.email || !searchParametersObject.token) {
    return (
      <div className="autologin">
        <h1>Missing Autologin Credentials</h1>
        <p className="fw--regular">
          Please make sure that there are valid "email" and "token" credentials included in the URL.
        </p>
        <Link to="/" className="btn btn--fluid btn--primary">
          Go Back
        </Link>
      </div>
    );
  }

  return (
    <div className="autologin">
      <h1>Logging In as:</h1>
      <p>{formattedEmail}</p>
      {isPending ? <Loader size="lg" /> : null}
    </div>
  );
};

export default Autologin;
